// Atlas data — the static globe pack on R2 (scripts/generate_globe_pack.py).
// Derived layers only; no live database at runtime.
import { loadStaticData } from "./staticData";

export type Grade = "A" | "B" | "C" | "D" | "F";

export interface AtlasHospital {
  id: string;
  n: string;          // name as filed
  c: string;          // city
  s: string;          // state
  la: number;
  lo: number;
  g: Grade | null;    // hospital-level price grade (median-baseline scale); null = ungraded
  mk?: number;        // median markup % vs the national median
  rk?: number;        // overall rank, 1 = most expensive
  rt?: number;        // ranked total
  cs?: number;        // CMS overall star 1–5
  rp?: number;        // HCAHPS "would definitely recommend" %
  ew?: number;        // CMS OP-18b median ER minutes
  gr?: number;        // Google rating
  gn?: number;        // Google review count
  ty?: "acute" | "critical" | "children" | "psych" | "rural_er" | "va" | "ltc" | "other";
  ow?: "nonprofit" | "forprofit" | "government" | "other";
  bd?: number;        // beds
  er?: boolean;       // emergency services
  sy?: string;        // system
  vy?: number;        // file vintage year
  np?: string; cn?: string; ei?: string;   // NPI, Medicare CCN, IRS EIN (searchable)
  fr?: boolean;       // flat-rate filer
  // CMS sub-ratings vs the national rate: +1 better, 0 no different, -1 worse
  mr?: -1 | 0 | 1; rr?: -1 | 0 | 1; sr?: -1 | 0 | 1;
  mo?: number;        // hospital-wide 30-day mortality % (Hybrid_HWM, lower is better)
  ra?: number;        // hospital-wide 30-day readmission % (Hybrid_HWR, lower is better)
  hai?: number;       // central-line infection ratio (HAI_1_SIR, 1.0 = national)
  // finances (CMS HCRIS cost report, latest fiscal year fy; IRS 990 for ceo/ar)
  fy?: number; nr?: number; mcr?: number; om?: number; cc?: number;
  ar?: number; ry?: number; ceo?: number;
  // price-file stats from the rankings
  tp?: number; ta?: number; ep?: number; op?: number;
  cptm?: number; hcm?: number; drgm?: number; revm?: number; ndcm?: number;
}

export interface HospitalPack {
  generated_at: string;
  count: number;
  ranked_total: number;
  hospitals: AtlasHospital[];
}

export interface AtlasScenario {
  id: string; slug: string; n: string; cat: string;
  fam?: string; var?: string; sev?: number;
  nc?: number;        // national total (the scenario page's estimated cost); also the blend baseline
  nb?: number; b10?: number; b50?: number; b90?: number;   // hospital-bill band: count, 10th / 50th / 90th percentile
}
export interface ScenarioPack { generated_at: string; count: number; scenarios: AtlasScenario[] }

interface ZipShard { z: Record<string, [number, number]> }
const zipShards = new Map<string, Promise<ZipShard | null>>();
/** ZIP centroid from the Census ZCTA gazetteer shards on R2. */
export async function lookupZip(zip: string): Promise<{ la: number; lo: number } | null> {
  if (!/^\d{5}$/.test(zip)) return null;
  const d = zip[0];
  if (!zipShards.has(d)) {
    zipShards.set(d, loadStaticData<ZipShard>(`globe/zips/${d}.json`, { cdnOnly: true }).catch(() => null));
  }
  const shard = await zipShards.get(d)!;
  if (!shard) { zipShards.delete(d); return null; }
  const hit = shard.z[zip];
  return hit ? { la: hit[0], lo: hit[1] } : null;
}

let hospitalsPromise: Promise<HospitalPack | null> | null = null;
export function loadHospitalPack(): Promise<HospitalPack | null> {
  if (!hospitalsPromise) {
    hospitalsPromise = loadStaticData<HospitalPack>("globe/hospitals.json", { cdnOnly: true })
      .catch((e) => { console.warn("[atlas] hospital pack failed", e); return null; })
      .then((p) => { if (!p) hospitalsPromise = null; return p; });
  }
  return hospitalsPromise;
}

let scenariosPromise: Promise<ScenarioPack | null> | null = null;
export function loadScenarioPack(): Promise<ScenarioPack | null> {
  if (!scenariosPromise) {
    scenariosPromise = loadStaticData<ScenarioPack>("globe/scenarios.json", { cdnOnly: true })
      .catch((e) => { console.warn("[atlas] scenario pack failed", e); return null; })
      .then((p) => { if (!p) scenariosPromise = null; return p; });
  }
  return scenariosPromise;
}

export const TYPE_LABEL: Record<NonNullable<AtlasHospital["ty"]>, string> = {
  acute: "Acute care", critical: "Critical access", children: "Children's", psych: "Psychiatric",
  rural_er: "Rural emergency", va: "Veterans Affairs", ltc: "Long-term care", other: "Other",
};
export const OWN_LABEL: Record<NonNullable<AtlasHospital["ow"]>, string> = {
  nonprofit: "Non-profit", forprofit: "For-profit", government: "Government", other: "Other",
};

/** Great-circle distance in statute miles. */
export function milesBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 3958.8, d = Math.PI / 180;
  const dLat = (bLat - aLat) * d, dLon = (bLon - aLon) * d;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * d) * Math.cos(bLat * d) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
